import { http } from "@/utils/http"

// ==================== 教师认证相关 ====================
export interface TeacherLoginReq {
  account: string
  password: string
}

export interface TeacherInfo {
  id: number
  account: string
  name: string
  orgId: number
  orgAdmin: number
  orgName: string
  maxkeyId: string
  phone: string
}

export interface TeacherLoginResp {
  token: string
  info: TeacherInfo
}

// 教师登录
export const teacherLogin = (data: TeacherLoginReq) => {
  return http.request<TeacherLoginResp>("post", "/api/v1/console/auth/login", {
    data
  })
}

// 教师登出
export const teacherLogout = () => {
  return http.request("get", "/api/v1/console/auth/logout")
}

// 获取教师信息
export const getTeacherInfo = () => {
  return http.request<TeacherInfo>("get", "/api/v1/console/auth/info")
}

// ==================== 角色权限相关 ====================
export interface RoleItem {
  id: number
  name: string // 对应数据库remark字段，实际的角色名称
  tag: string
  remark: string // 保留字段用于兼容
  perms: string
  isSuper: number
}

export interface PermissionItem {
  id: number
  pid: number
  url: string
  menuName: string
  menuType: string
  routeName: string
  routePath: string
  icon: string
  component: string
  perms: string
  noCache: number
  hidden: number
  childrenName: string
  parentName: string
  sort: number
  isAllot: number
  noColumn: number
  levelHidden: number
  isExcept: number
}

// 获取角色列表
export const getRoleList = () => {
  return http.request<RoleItem[]>("get", "/api/v1/console/role/list")
}

// 获取权限列表
export const getPermissionList = () => {
  return http.request<PermissionItem[]>("get", "/api/v1/console/permission/list")
}

// 获取我的权限列表
export const getMyPermissions = (course_id?: number) => {
  const url = course_id
    ? `/api/v1/console/permission/myList?course_id=${course_id}`
    : "/api/v1/console/permission/myList"
  return http.request<PermissionItem[]>("get", url)
}

// ==================== 课程相关 ====================
export interface CourseItem {
  id: number
  name: string
  code: string
  module: string
  theoryHours: number
  practiceHours: number
  targetAudience: string
  questionBankType: string
  description?: string
  status: number
  createTime: string
  updateTime: string
  courseCount: number
  groupName?: string
  group_name?: string
}

export interface CourseListResp {
  list: CourseItem[]
  total: number
}

// 学科与课程管理专用接口
export interface SaveCourseReq {
  id?: number
  name: string
  catId: number
  groupName?: string
}

// 获取课程列表
export const getCourseList = (data: { page?: number; pageSize?: number }) => {
  return http.request<CourseListResp>("post", "/api/v1/console/course/courseListWithCat", {
    data
  })
}

// 按学科分页获取课程列表（管理用）
export interface CoursePageListReq {
  catId?: number
  page?: number
  size?: number
  name?: string
  code?: string
  module?: string
}

export const getCoursePageList = (data: CoursePageListReq) => {
  return http.request<CourseListResp>("post", "/api/v1/console/course/list", { data })
}

// 获取课程列表带分类（用于浏览试题）
export interface CourseListWithCatItem {
  id: number
  name: string
  courses: CourseItem[]
  children: CourseListWithCatItem[]
}

export const getCourseListWithCat = (data?: {}) => {
  return http.request<CourseListWithCatItem[]>("post", "/api/v1/console/course/courseListWithCat", {
    data: data || {}
  })
}

// 所有课程树（统计-结构使用）
export const getCourseListWithCatAll = () => {
  return http.request<CourseListWithCatItem[]>("post", "/api/v1/console/course/courseListWithCatAll")
}

// 获取简单课程列表
export interface SimpleCourseItem {
  id: number
  name: string
}

export const getCourseSimpleList = () => {
  return http.request<SimpleCourseItem[]>("get", "/api/v1/console/course/simpleList")
}

// 新增/编辑 课程
export const saveCourse = (data: SaveCourseReq) => {
  return http.request("post", "/api/v1/console/course/saveCourse", { data })
}

// 删除课程
export const deleteCourse = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/course/delCourse", { data })
}

// ==================== 题目相关 ====================
export interface QuestionType {
  id: number
  name: string
  courseId: number
  sort: number
}

export interface QuestionDifficulty {
  id: number
  name: string
  courseId: number
  level: number
}

export interface QuestionItem {
  id: number
  creatorId: number
  creator: string
  auditorId?: number
  auditor?: string
  auditTime?: number
  courseId: number
  qtTypeId: number
  qtTypeName: string
  qtTypeCat: number
  qtDiffId: number
  qtDiffName: string
  qtRankId: number
  qtRankName: string
  qtKpId: number
  qtKpName: string
  year?: number
  refCount?: number
  qtRequirement?: string // 题目要求（目标层次）
  state: number
  children?: string
  childrenDetail?: QuestionMixed[]
  source?: string
  area?: string
  answer: string
  blanks: number
  qtHtml: string // 题干HTML
  asHtml?: string // 答案HTML
  anHtml?: string // 分析HTML
  optionaHtml?: string // 选项A HTML
  optionbHtml?: string // 选项B HTML
  optioncHtml?: string // 选项C HTML
  optiondHtml?: string // 选项D HTML
  optioneHtml?: string // 选项E HTML
  optionfHtml?: string // 选项F HTML
  createdTime: number
  updatedTime: number
}

export interface QuestionListReq {
  courseId?: number
  typeId?: number
  diffId?: number
  rankId?: number
  kpIds?: number[]
  indexText?: string
  createBegin?: number
  createEnd?: number
  questionId?: number
  excludeArr?: number[]
  page: number
  size: number
}

export interface QuestionListResp {
  list: QuestionItem[]
  total: number
}

// 引用记录相关接口
export interface QuoteLogReq {
  questionId: number
  page: number
  size: number
}

export interface QuoteLogItem {
  paperId: number
  paperName: string
  paperType: number
  creator: string
  createTime: number
}

export interface QuoteLogResp {
  total: number
  list: QuoteLogItem[]
}

// 获取题型列表
export const getQuestionTypes = (data: { id: number }) => {
  return http.request<QuestionType[]>("post", "/api/v1/console/types/courseTypes", {
    data
  })
}

// 获取题目引用记录
export const getQuoteLog = (data: QuoteLogReq) => {
  return http.request<QuoteLogResp>("get", "/api/v1/console/paper/quoteLog", {
    params: data
  })
}

// 获取难度列表
export const getQuestionDifficulties = (data: { id: number }) => {
  return http.request<{ list: QuestionDifficulty[] }>("post", "/api/v1/console/diff/courseDiffs", {
    data
  })
}

// 获取题目列表
export const getQuestionList = (data: QuestionListReq) => {
  return http.request<QuestionListResp>("post", "/api/v1/console/question/list", {
    data
  })
}

// 保存题目
export const saveQuestion = (data: Partial<QuestionItem>) => {
  return http.request("post", "/api/v1/console/question/save", { data })
}

// 删除题目
export const deleteQuestion = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/question/delete", { data })
}

// 题目重复检测
export interface QuestionRepeatCheckReq {
  courseId: number
  qtTypeId: number
  indexText: string
  repeatPercent: number // 50-100
  excludeArr?: number[] // 排除题目ID数组
}

export interface QuestionRepeatCheckResp {
  list: QuestionPageItem[]
}

export const checkQuestionRepeat = (data: QuestionRepeatCheckReq) => {
  return http.request<QuestionRepeatCheckResp>("post", "/api/v1/console/question/repeatCheck", { data })
}

// ==================== 试卷相关 ====================
export interface PaperItem {
  id: number
  courseId: number
  title: string
  paperType: string
  creatorId: number
  creator: string
  creatorAccount: string
  auditorId?: number
  auditor?: string
  auditorAccount?: string
  totalPoints: number
  mixedOrder: number
  state: number
  schemeId?: number
  schemeName?: string
  pub: number
  children?: string
  childrenPaper?: PaperItem[]
  isUnion: number
  createdTime: number
  updatedTime: number
  printout_url?: string
  unit_audit_opinion?: string
  expert_audit_opinion?: string
  proposition_config?: string
  proposition_info_table?: string
  expired?: number // 是否过期 0正常 1过期
  useCount?: number // 选用次数
}

export interface PaperListReq {
  page: number
  size: number
  courseId?: number
  paperType?: string
  title?: string
  createBegin?: number
  createEnd?: number
  unusedTime?: number // 未使用时间 时间戳
}

export interface PaperListResp {
  total: number
  list: PaperItem[]
}

export interface PaperQuestioSaveItem {
  questionId: number
  points: number
}

export interface PaperSaveReq {
  id?: number
  courseId: number
  title: string
  paperType: string
  mixedOrder: number
  state: number
  schemeId?: number
  schemeName?: string
  pub: number
  details: PaperQuestioSaveItem[]
}

export interface CombinePaperReq {
  mainPaperId: number
  subPaperIds: number[]
}

// 获取试卷列表
export const getPaperList = (data: PaperListReq) => {
  return http.request<PaperListResp>("get", "/api/v1/console/paper/list", {
    params: data
  })
}

// 保存试卷
export const savePaper = (data: PaperSaveReq) => {
  return http.request("post", "/api/v1/console/paper/save", { data })
}

// 删除试卷
export const deletePaper = (data: { id: number; password?: string }) => {
  return http.request("post", "/api/v1/console/paper/del", { data })
}

// 获取试卷详情
export const getPaperDetail = (data: { id: number }) => {
  return http.request("get", `/api/v1/console/paper/questionsByPaperID?id=${data.id}`)
}

// 添加试卷使用历史
export interface IDJsonReq {
  id: number
}

// 获取命题角色
export interface PropositionRoleResp {
  character: string
}

export const getPropositionRole = (data: IDJsonReq) => {
  return http.request<PropositionRoleResp>("post", "/api/v1/console/paper/getPropositionRole", {
    data
  })
}

export interface AddUseHistoryReq {
  id: number
  comment?: string
  device?: string
  rule?: string
}

export const addPaperUseHistory = (data: AddUseHistoryReq) => {
  return http.request("post", "/api/v1/console/paper/addUseHistory", { data })
}

// 获取试卷使用历史
export interface UseHistoryItem {
  paperId: number
  paperName: string
  paperType: string
  creator: string
  chooser: string
  createTime: number
  comment: string
  device: string
  rule: string
}

export interface UseHistoryResp {
  list: UseHistoryItem[]
}

export const getPaperUseHistory = (data: IDJsonReq) => {
  return http.request<UseHistoryResp>("get", "/api/v1/console/paper/getUseHistory", {
    params: data
  })
}

// 设置试卷过期状态
export const setPaperExpired = (data: { id: number; expired: number }) => {
  return http.request("post", "/api/v1/console/paper/setExpired", { data })
}

// 获取试卷题目列表
export interface PaperQuestion {
  id: number
  paperId: number
  questionId: number
  orderNum: number
  points: number
  courseId: number
  qtTypeId: number
  qtTypeCat: number
  qtDiffId: number
  qtRankId: number
  qtKpId: number
  qtRequirement: string
  year: number
  refCount: number
  children: string
  childrenMx: QuestionMixed[]
  source: string
  area: string
  answer: string
  blanks: number
  qtHtml: string
  asHtml: string
  anHtml: string
  optionaHtml: string
  optionbHtml: string
  optioncHtml: string
  optiondHtml: string
  optioneHtml: string
  optionfHtml: string
  creator: string
  courseName: string
  typeName: string
  rankName: string
  diffName: string
  diffSort: number // 难度排序
  kpName: string
  kpRequirement: string // 知识点要求（考试目标）
  kpSort: number // 知识点排序
}


export interface QuoteLogResp {
  total: number
  list: QuoteLogItem[]
}

export const getQuestionQuoteLog = (params: { questionId: number; page?: number; size?: number }) => {
  const { questionId, page = 1, size = 10 } = params
  return http.request<QuoteLogResp>("get", "/api/v1/console/paper/quoteLog", {
    params: { questionId, page, size }
  })
}

export const getQuestionsByPaperID = (data: { id: number }) => {
  return http.request<PaperQuestion[]>("get", `/api/v1/console/paper/questionsByPaperID?id=${data.id}`)
}

// 提交审核相关接口
export interface PaperPropositionInfoTable {
  paper_type: string // A/B
  course_nature: string // 必修|选修|限选|自修
  exam_date: string // yyyy-mm-dd hh:ii
  exam_duration: string // xx 分钟
  course_name: string
  teaching_unit: string
  grade_level: string // 本科|大专|任职|硕士|博士
  teacher: string
  proposition_teacher: string
  proposition_way: string // 试题库|试卷库|专家制卷|其他
  teaching_separation: string // 是|否
  analysis_table: string
  paper_total_score: string
  expected_average_score: string
  audit_items: number[] // 12项审核结果 (0未填写 1是 2否)
}

export interface QuestionAnalysisTable {
  question_number: string // 试卷编号
  question_type: string // 题型
  question_score: string // 分值
  question_difficulty: string // 难度
  question_target: string // 考试目标（多个用、分隔）
  knowledge_point: string // 知识点
  qt_id: number // 题目ID
}

export interface SubmitUnitAuditReq {
  paperId: number
  paperPropositionInfoTable: PaperPropositionInfoTable
}


export const submitUnitAudit = (data: SubmitUnitAuditReq) => {
  return http.request("post", "/api/v1/console/paper/submitUnitAudit", { data })
}

// 变更试卷状态
export interface SwitchPaperStateReq {
  paper_id: number
  state: number
}

export const switchPaperState = (data: SwitchPaperStateReq) => {
  return http.request("post", "/api/v1/console/proposition/switchPaperState", { data })
}

// 教研室审核
export interface UnitAuditReq {
  paperId: number
  unitAuditOpinion: string
  auditItems: number[] // 12项审核结果 (0未填写 1是 2否)
  paperPropositionInfoTable?: PaperPropositionInfoTable
}

export const unitAudit = (data: UnitAuditReq) => {
  return http.request("post", "/api/v1/console/paper/unitAudit", { data })
}

// 上传打印稿
export const uploadPrintout = (data: FormData) => {
  return http.request("post", "/api/v1/console/paper/uploadPrintout", {
    data,
    headers: {
      "Content-Type": "multipart/form-data"
    }
  })
}

// 确认打印稿
export const confirmPrintout = (data: { paperId: number }) => {
  return http.request("post", "/api/v1/console/paper/comfirmPrintout", { data })
}

// 下载打印稿
export const downloadPrintoutReq = (params: { paperId: number; fileType: "paper" | "answer" }) => {
  return http.request<Blob>(
    "post",
    "/api/v1/console/paper/downloadPrintout",
    {
      data: {
        paperId: params.paperId,
        fileType: params.fileType
      }
    },
    {
      responseType: "blob"
    }
  )
}

// 导出前检查
export const exportPreCheck = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/paper/exportPreCheck", { data })
}

// 合并试卷
export const combinePaper = (data: CombinePaperReq) => {
  return http.request("post", "/api/v1/console/paper/combinePaper", { data })
}

// 获取公共试卷列表
export const getPublicPapers = (courseId: number) => {
  return http.request("get", `/api/v1/console/paper/publicPapers?id=${courseId}`)
}

// 获取试卷类型列表
export const getPaperTypes = (courseId: number) => {
  return http.request<string[]>("post", "/api/v1/console/paper/types", {
    data: { id: courseId }
  })
}

// ==================== 考试相关 ====================
export interface ExamItem {
  id: number
  courseId: number
  courseName: string
  name: string
  description: string
  paperType: number // 1:方案 2:试卷
  examType: number // 1:正式考试 2:练习自测
  paperId: number
  paperName: string
  randomOrder: number // 1:是 2:否
  schemeId: number
  schemeName: string
  startTime: number
  endTime: number
  pickUpTime: number
  creatorId: number
  creator: string
  state: number // 1:未开始 2:考试中 3:收卷 4:阅卷完成
  autoCorrect: number // 1:自动评分 2:不自动评分
  autoCorrectState: number // 1:未结束 2:已结束
  arrangeState: number // 1:完成 2:未完成
  allowReview: number // 1:允许 2:不允许
  practiseCount: number // 0:不限次数
  rePractiseMark: number // 1:首次 2:末次 3:平均 4:最高 5:最低
  passScore: number // 及格线 *100
  failedNotice: string
  excellentScore: number // 优秀线 *100
  multiSelectType: number // 1:零分 2:满分 3:一半分 4:自定义
  multiSelectPoint: number // *100
  createdTime: number
  updatedTime: number
  // 额外的统计字段（非数据库字段）
  duration?: number
  totalScore?: number
  studentCount?: number
  submitCount?: number
  avgScore?: number
}

export interface ExamListReq {
  page: number
  size: number
  courseId?: number
  startTime?: number
  endTime?: number
  state?: number // 考试状态 1未开始 2进行中 3考试结束 4阅卷完成
  key?: string
  examType?: number
  paperType?: number
}

export interface ExamListResp {
  total: number
  list: ExamItem[]
}

export interface ExamAddReq {
  courseId: number // 课程ID
  examName: string // 考试名称
  examType: number // 考试类型 1:正式考试 2:练习自测
  paperType: number // 考试方式 1:方案 2:试卷
  paperId: number // 试卷ID
  schemeId: number // 方案ID
  allowReview: number // 交卷后查看 1:允许 2:不允许
  randomOrder: number // 随机顺序 1:是 2:不是
  passScore: number // 及格线
  excellentScore: number // 优秀线
  failedNotice: string // 不及格提示
  practiseCount: number // 练习允许重考次数 0:不限次数
  autoCorrect: number // 客观题 1:自动评分 2:不自动评分
  startTime: number // 开始时间 时间戳(秒)
  endTime: number // 结束时间 时间戳(秒)
  practiseTimeInterval: number // 练习时间间隔 单位秒 0:不限时间
  rePractiseMark: number // 练习重考成绩标准 1:首次 2:末次 3:平均 4:最高 5:最低
  multiSelectType: number // 多选题类型 1:零分 2:一半分 3:自定义分值
  multiSelectPoint: number // 多选题自定义分数
  StudentRange: number[] // 学生ID范围
  ClassRange: number[] // 班级ID范围
}

export interface StudentScoreItem {
  id: number
  studentId: number
  studentName: string
  studentAccount: string
  className: string
  score: number
  submitTime: string
  duration: number
  rank: number
  status: number // 0:未参加 1:已提交 2:缺考
}

export interface ScoreListReq {
  examId: number
  keyword?: string
  page: number
  pageSize: number
}

export interface ScoreListResp {
  list: StudentScoreItem[]
  total: number
  avgScore: number
  maxScore: number
  minScore: number
  submitRate: number
}

// 获取考试列表
export const getExamList = (data: ExamListReq) => {
  return http.request<ExamListResp>("post", "/api/v1/console/exam/list", {
    data
  })
}

// 获取考试详情（通过 list 接口单个查询）
export const getExamDetail = (examId: number, courseId: number) => {
  return http.request<ExamListResp>("post", "/api/v1/console/exam/list", {
    data: {
      page: 1,
      size: 1,
      courseId,
      key: ""
    }
  }).then(res => {
    // 从列表中找到对应的考试
    const exam = res?.list?.find(e => e.id === examId)
    return exam || null
  })
}

// 添加考试
export const addExam = (data: ExamAddReq) => {
  return http.request("post", "/api/v1/console/exam/add", { data })
}

// 删除考试
export const deleteExam = (id: number) => {
  return http.request("post", "/api/v1/console/exam/del", {
    data: { id }
  })
}

// ==================== 考试批改相关 ====================
// 考生信息
export interface ExamStudentInfo {
  id: number
  examId: number
  stuId: number
  stuName: string
  stuAccount: string
  major: string
  classId: number
  className: string
  state: number // 1未登录 2考试中 3交卷 4缺考
  score: number // 总分 /100
  objectScore: number // 客观题分数 /100
  correctState: number // 批改状态 0未批改完成 1批改完成
}

// 获取考试考生列表
export const getExamStudents = (examId: number) => {
  return http.request<{ list: ExamStudentInfo[] }>("post", "/api/v1/console/exam/examStudents", {
    data: { examId }
  })
}

// AI 批改参数
export interface AiCorrectReq {
  question: string
  answer: string
  student_response: string
  max_score: number
}

export interface AiCorrectResp {
  message: string
  score: number
  grading_basis: string
}

// 获取AI评卷
export const getAiCorrect = (data: AiCorrectReq) => {
  return http.request<AiCorrectResp>("post", "/api/v1/console/exam/getAiCorrect", {
    data
  })
}

// 批改一道题目
export const correctOne = (id: number, points: number) => {
  return http.request("post", "/api/v1/console/exam/correctOne", {
    data: { id, points }
  })
}

// 设置一份试卷批改完成
export const setOneCorrect = (id: number) => {
  return http.request("post", "/api/v1/console/exam/setOneCorrect", {
    data: { id }
  })
}

// 设置所有试卷批改完成
export const setAllCorrect = (id: number) => {
  return http.request("post", "/api/v1/console/exam/setAllCorrect", {
    data: { id }
  })
}

// 开始考试
export const startExam = (id: number) => {
  return http.request("post", "/api/v1/console/exam/start", {
    data: { id }
  })
}

// 结束考试
export const endExam = (id: number) => {
  return http.request("post", "/api/v1/console/exam/end", {
    data: { id }
  })
}

// 发送考试消息
export const sendExamMessage = (data: { examId: number; message: string }) => {
  return http.request("post", "/api/v1/console/exam/sendMsg", {
    data
  })
}

// 强制收卷单个考生
export const forceSubmitExamStudent = (examStudentId: number) => {
  return http.request("post", "/api/v1/console/exam/endOne", {
    data: { id: examStudentId }
  })
}

// 考试补时
export const examAddTime = (id: number, extraMinutes: number) => {
  return http.request("post", "/api/v1/console/exam/addTime", {
    data: { id, extraMinutes }
  })
}

// 获取考试学生信息
export const getExamStudentsInfo = (data: { id: number }) => {
  return http.request<StudentsInfoResp>("post", "/api/v1/console/exam/studentsInfo", { data })
}

// 获取学生成绩列表
export const getStudentScoreList = (data: ScoreListReq) => {
  return http.request<ScoreListResp>("post", "/api/v1/console/exam/scoreList", {
    data
  })
}

// 获取学生试卷详情（用于批改）
export const getStudentPaper = (data: { examId: number; stuId: number }) => {
  return http.request<StudentPaperResp>("post", "/api/v1/console/exam/studentPaper", { data })
}

// 批改一道题目
export const correctOneQuestion = (data: {
  examId: number
  studentId: number
  questionId: number
  score: number
  comment?: string
}) => {
  return http.request("post", "/api/v1/console/exam/correctOne", { data })
}

// ==================== 统计相关 ====================
export interface StatisticsData {
  totalStudents: number
  totalExams: number
  totalQuestions: number
  totalCourses: number
  recentExams: ExamItem[]
  scoreDistribution: Array<{
    range: string
    count: number
  }>
}

// 获取统计数据
export const getStatistics = () => {
  return http.request<StatisticsData>("get", "/api/v1/console/statistics/dashboard")
}

// ==================== 统计（课程/题量 总览） ====================
export interface CourseLiteStat {
  courseName: string
  createdAt: string
  subjectName: string
  courseUnit: string
  creatorName?: string
}

export interface CourseSubjectAggItem {
  subjectId: number
  subjectName: string
  total: number
  courses?: CourseLiteStat[]
}

export interface CourseLiteDelStat extends CourseLiteStat {
  deletedTime: number
  deletedUser: string
}

export interface CourseYearlyAggItem {
  year: string
  add: number
  del: number
  endCount: number
  yearAddCourses?: CourseLiteStat[]
  yearDelCourses?: CourseLiteDelStat[]
}

export interface CourseTotalStatisticsResp {
  subjects: CourseSubjectAggItem[]
  yearly: CourseYearlyAggItem[]
}

export interface QuestionSubjectCourseItem {
  count: number
  courseId: number
  courseName: string
  subjectName: string
  updateRate?: number
  groupName: string
  questionBankType: string // 题库类型: 题库｜卷库
  yearBeginCount?: number
  yearEndCount?: number
  yearAddCount?: number
  yearDelCount?: number
}

export interface QuestionSubjectAggItem {
  subjectName: string
  courseCount: number
  questionCount: number
  desc?: QuestionSubjectCourseItem[]
}

export interface QuestionYearlyAggItem {
  year: string
  yearBeginCount: number
  yearEndCount: number
  yearUpdateCount: number
  yearAddCount: number
  yearDelCount: number
  updateRate: number
  desc?: QuestionSubjectCourseItem[]
}

export interface QuestionTotalStatisticsResp {
  subjects: QuestionSubjectAggItem[]
  yearly: QuestionYearlyAggItem[]
}

// 轻量请求去重 + 短期缓存（避免同页面多个子组件并发触发重复请求）
const __statInFlight: Record<string, { ts: number; promise: Promise<any>; data?: any }> = {}
function requestWithCache<T>(key: string, fetcher: () => Promise<T>, ttlMs = 60_000): Promise<T> {
  const now = Date.now()
  const entry = __statInFlight[key]
  if (entry) {
    if (entry.data && now - entry.ts < ttlMs) return Promise.resolve(entry.data as T)
    return entry.promise as Promise<T>
  }
  const p = fetcher()
    .then((res) => {
      __statInFlight[key] = { ts: now, promise: Promise.resolve(res), data: res }
      return res
    })
    .catch((e) => {
      delete __statInFlight[key]
      throw e
    })
  __statInFlight[key] = { ts: now, promise: p }
  return p
}

export interface ForceUpdateReq {
  forceUpdate?: boolean
}

export const getCourseTotalStatistics = (data?: ForceUpdateReq) => {
  return http.request<CourseTotalStatisticsResp>("post", "/api/v1/console/statistics/courseTotalStatistics", {
    data: data || { forceUpdate: false }
  })
}

export const getQuestionTotalStatistics = (data?: ForceUpdateReq) => {
  return http.request<QuestionTotalStatisticsResp>("post", "/api/v1/console/statistics/questionTotalStatistics", {
    data: data || { forceUpdate: false }
  })
}

// 结构统计
export interface StructureStatisticsReq {
  courseId?: number
  kpArr?: number[]
}
export interface StructureTypeAggItem { count: number; typeId: number; typeName: string; typeCategory?: number; typeMinName?: string }
export interface StructureRankAggItem { count: number; rankId: number; rankName: string }
export interface StructureLevelAggItem { count: number; levelId: number; levelName: string }
export interface StructureKnowledgeAggItem { count: number; knowledgeId: number; knowledgeName: string; typeCategory?: number }
export interface StructureStatisticsResp {
  questionTypes: StructureTypeAggItem[]
  questionRanks: StructureRankAggItem[]
  questionLevels: StructureLevelAggItem[]
  knowledgePoints: StructureKnowledgeAggItem[]
}
export const getStructureStatistics = (data: StructureStatisticsReq) => {
  return http.request<StructureStatisticsResp>("post", "/api/v1/console/statistics/structureStatistics", { data })
}

// ==================== 仪表板统计相关 ====================
export interface DashboardStatsReq {
  id: number // 课程ID
}

export interface CurCourseRecentExam {
  id: number
  name: string
  status: number // 1未开始 2考试中 3收卷 4阅卷完成
  start_time: number
  stu_count: number // 参加考试学生人数
}

export interface RecentQuestionInputStat {
  date: string
  count: number
}

export interface DashboardStatsData {
  course_count: number
  stu_count: number
  teacher_count: number
  cur_course_paper_count: number
  cur_course_exam_count: number
  cur_course_question_count: number
  cur_course_recent_exams: CurCourseRecentExam[]
  recent_question_input_stats: RecentQuestionInputStat[]
}

export interface DashboardStatsResp {
  code: number
  time: number
  msg: string
  data: DashboardStatsData
}

// 获取仪表板统计数据
export const getDashboardStats = (data: DashboardStatsReq) => {
  return http.request<DashboardStatsData>("post", "/api/v1/console/dashboard/stats", {
    data
  })
}

// 增加试卷导出计数
export const incPaperExportCount = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/paper/incExportCount", { data })
}

// 获取试卷头部HTML（渲染后的模板）
export interface PaperHeaderResp {
  html: string
}
export const getPaperHeader = (data: { id: number }) => {
  return http.request<PaperHeaderResp>("post", "/api/v1/console/paper/getPaperHeader", { data })
}

// ==================== 学生管理相关 ====================
export interface StudentItem {
  id: number
  name: string
  account: string
  classId: number
  className: string
  major: string
  group: string
  disabled: number
  createTime: string
}

export interface StudentListReq {
  classId?: number
  keyword?: string
  page: number
  pageSize: number
}

export interface StudentListResp {
  list: StudentItem[]
  total: number
}

// 获取学生列表
export const getStudentList = (data: StudentListReq) => {
  return http.request<StudentListResp>("post", "/api/v1/console/student/list", {
    data
  })
}

// 获取班级列表
export const getClassList = () => {
  return http.request<Array<{ id: number; name: string }>>("get", "/api/v1/console/class/list")
}

// ==================== 权限管理相关 ====================

// 保存权限
export interface SavePermissionReq {
  id?: number
  pid: number
  url?: string
  menuName: string
  menuType: string
  routeName: string
  routePath?: string
  icon?: string
  component: string
  perms?: string
  noCache?: number
  hidden?: number
  childrenName?: string
  parentName?: string
  sort?: number
  isAllot?: number
  noColumn: number
  levelHidden: number
  isExcept: number
}

export const savePermission = (data: SavePermissionReq) => {
  return http.request("post", "/api/v1/console/permission/save", { data })
}

export const deletePermission = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/permission/delete", { data })
}

// ==================== 角色管理相关 ====================

export interface SaveRoleReq {
  id?: number
  name: string // 角色名称，对应数据库remark字段
  tag: string
  remark?: string // 兼容字段
  perms: number[]
  isSuper: number // 保留用于兼容，实际使用tag判断角色类型
}

export const saveRole = (data: SaveRoleReq) => {
  return http.request("post", "/api/v1/console/role/save", { data })
}

export const deleteRole = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/role/delete", { data })
}

export const getRolePerms = (roleTag: string) => {
  return http.request<PermissionItem[]>("get", "/api/v1/console/role/perms", {
    params: { roleTag }
  })
}

// ==================== 用户管理相关 ====================

// 用户授权信息（关联查询的额外数据）
export interface UserGrantInfo {
  id: number
  course_id: number
  course_name: string // 课程名称（关联查询）
  role_id: number
  role_name: string
}

// 教师信息接口（严格按照teacher.sql定义 + 关联查询的额外数据）
export interface TeacherItem {
  id: number
  account: string // 账户
  name: string // 姓名
  password?: string // 密码（编辑时可选）
  disabled: number // 是否禁用 1禁用 2正常
  tag: string // 角色标签
  org_id: number // 部门ID
  org_admin: number // 部门管理员 1是 2否
  phone?: string // 联系号码
  last_ip?: string // 上次登录IP
  last_login_time?: number // 上次登录时间
  created_time?: number // 创建时间
  updated_time?: number // 更新时间
  deleted_time?: number // 删除时间
  // 关联查询的额外数据
  org_name?: string // 部门名称（关联查询）
  grants?: UserGrantInfo[] // 授权信息（关联查询）
}

// 教师列表请求参数
export interface TeacherListReq {
  page: number
  size: number
  keyword?: string // 关键词搜索（账户或姓名）
  tag?: string // 角色标签筛选
  org_id?: number // 部门筛选
  disabled?: number // 状态筛选（1禁用 2正常）
}

// 教师列表响应
export interface TeacherListResp {
  list: TeacherItem[]
  total: number
}

// 保存教师请求参数
export interface SaveTeacherReq {
  id?: number
  account: string // 账户
  name: string // 姓名
  password?: string // 密码（新增时必填，编辑时可选）
  disabled: number // 是否禁用 1禁用 2正常
  tag: string // 角色标签
  org_id: number // 部门ID
  org_admin: number // 部门管理员 1是 2否
  phone?: string // 联系号码
}

// 删除教师请求参数
export interface DeleteTeacherReq {
  ids: number[]
}

// 重置密码请求参数
export interface ResetPasswordReq {
  id: number
  newPassword: string
}

// 更新教师状态请求参数
export interface UpdateTeacherStatusReq {
  id: number
  disabled: number // 1禁用 2正常
}

// 简单角色信息
export interface SimpleRole {
  id: number
  name: string
}

// 简单组织信息
export interface SimpleOrg {
  id: number
  name: string
}

// 简单组织列表响应
export interface SimpleOrgListResp {
  list: SimpleOrg[]
}

// ==================== 授权管理相关接口 ====================

// 获取用户授权列表请求
export interface UserGrantListReq {
  teacher_id: number // 教师ID
}

// 获取用户授权列表响应
export interface UserGrantListResp {
  list: UserGrantInfo[]
}

// 保存用户授权请求
export interface UserGrantSaveReq {
  id?: number // 授权记录ID（编辑时提供）
  teacherId: number // 教师ID
  courseId: number // 课程ID
  roleId: number // 角色ID
}

// 删除用户授权请求
export interface UserGrantDeleteReq {
  id: number // 授权记录ID
}

// 批量保存用户授权请求
export interface UserGrantBatchSaveReq {
  teacherId: number // 教师ID
  grants: Array<{
    id?: number // 授权记录ID（编辑时提供）
    courseId: number // 课程ID
    roleId: number // 角色ID
  }>
}

// 获取教师列表（使用现有API地址）
export const getTeacherList = (data: TeacherListReq) => {
  return http.request<TeacherListResp>("get", "/api/v1/console/user/list", {
    params: data
  })
}

// 保存教师（使用现有API地址）
export const saveTeacher = (data: SaveTeacherReq) => {
  return http.request("post", "/api/v1/console/user/save", { data })
}

// 删除教师（使用现有API地址）
export const deleteTeacher = (data: DeleteTeacherReq) => {
  return http.request("post", "/api/v1/console/user/delete", { data })
}

// 重置教师密码（使用现有API地址）
export const resetTeacherPassword = (data: ResetPasswordReq) => {
  return http.request("post", "/api/v1/console/user/resetPassword", { data })
}

// 更新教师状态（使用现有API地址）
export const updateTeacherStatus = (data: UpdateTeacherStatusReq) => {
  return http.request("post", "/api/v1/console/user/updateStatus", { data })
}

// 获取简单角色列表
export const getSimpleRoleList = () => {
  return http.request<SimpleRole[]>("get", "/api/v1/console/role/simpleList")
}

// 获取简单组织列表
export const getOrgSimpleList = () => {
  return http.request<SimpleOrgListResp>("get", "/api/v1/console/org/simpleList")
}

// ==================== 授权管理API ====================

// 获取用户授权列表
export const getUserGrantList = (data: UserGrantListReq) => {
  return http.request<UserGrantListResp>("get", "/api/v1/console/grant/userGrantList", {
    params: data
  })
}

// 保存用户授权
export const saveUserGrant = (data: UserGrantSaveReq) => {
  return http.request("post", "/api/v1/console/user/saveGrant", { data })
}

// 删除用户授权
export const deleteUserGrant = (data: UserGrantDeleteReq) => {
  return http.request("post", "/api/v1/console/user/deleteGrant", { data })
}

// 批量保存用户授权
export const batchSaveUserGrant = (data: UserGrantBatchSaveReq) => {
  return http.request("post", "/api/v1/console/user/batchSaveGrant", { data })
}

// 用户授权项接口
export interface UserGrantItem {
  teacher_id: number // 用户ID
  course_id: number // 课程ID
  role_id: number // 角色ID
}

// 用户授权请求接口
export interface UserGrantReq {
  teacher_id: number // 用户ID（顶层）
  list: UserGrantItem[] // 授权列表
}

// 授权编辑
export const editUserGrant = (data: UserGrantReq) => {
  return http.request("post", "/api/v1/console/grant/editUserGrant", { data })
}

// 获取角色简单列表
export const getRoleSimpleList = () => {
  return http.request("get", "/api/v1/console/role/simpleList")
}

// ==================== 学科与课程管理 ====================

// 保存学科
export interface SaveCatReq {
  id?: number
  name: string
  parentId?: number
  sort?: number
}

export const saveCat = (data: SaveCatReq) => {
  return http.request("post", "/api/v1/console/cat/saveCat", { data })
}

// 获取学科树
export interface CatTreeNode {
  id: number
  name: string
  parentId: number
  sort?: number
  children?: CatTreeNode[]
}

export const getCatTree = () => {
  return http.request<CatTreeNode[]>("get", "/api/v1/console/cat/catTree")
}

// 删除学科
export interface DeleteCatReq {
  id: number
}

export const deleteCat = (data: DeleteCatReq) => {
  // 后端定义为 /delCat
  return http.request("post", "/api/v1/console/cat/delCat", { data })
}

// 获取简单学科列表
export interface SimpleCatItem {
  id: number
  name: string
}

export interface SimpleCatResp {
  list: SimpleCatItem[]
}

export const getSimpleCatList = () => {
  return http.request<SimpleCatResp>("get", "/api/v1/console/cat/simpleList")
}

// ==================== 兼容性别名 ====================

// 为了兼容现有代码，保留UserXXX别名
export type UserItem = TeacherItem
export type UserListReq = TeacherListReq
export type UserListResp = TeacherListResp
export type SaveUserReq = SaveTeacherReq
export type UserDeleteReq = DeleteTeacherReq
export type UserResetPasswordReq = ResetPasswordReq
export type UserStatusReq = UpdateTeacherStatusReq

// 兼容的API函数
export const getUserList = getTeacherList
export const saveUser = saveTeacher
export const deleteUser = deleteTeacher
export const resetUserPassword = resetTeacherPassword
export const updateUserStatus = updateTeacherStatus

// ==================== 前后端统一字段定义 ====================

// 严格按照teacher.sql字段定义，前后端完全统一
export interface UserInfo {
  id: number
  account: string // 账户
  name: string // 姓名
  password?: string // 密码
  disabled: number // 是否禁用 1禁用 2正常
  tag: string // 角色标签
  org_id: number // 部门ID
  org_admin: number // 部门管理员 1是 2否
  phone?: string // 联系号码
  last_ip?: string // 上次登录IP
  last_login_time?: number // 上次登录时间
  created_time?: number // 创建时间
  updated_time?: number // 更新时间
  deleted_time?: number // 删除时间
  // 关联查询的额外数据
  org_name?: string // 部门名称（关联查询）
  grants?: UserGrantInfo[] // 授权信息（关联查询）
}

export interface UserSaveReq {
  id?: number
  account: string // 账户
  name: string // 姓名
  password?: string // 密码（新增时必填，编辑时可选）
  disabled: number // 是否禁用 1禁用 2正常
  tag: string // 角色标签
  org_id: number // 部门ID
  org_admin: number // 部门管理员 1是 2否
  phone?: string // 联系号码
}

// ==================== 组织管理相关 ====================

export interface OrgTreeNode {
  id: number
  name: string
  code: string
  parentId: number
  sort: number
  status: number // 1:启用 0:禁用
  user_count: number // 用户数量
  children: OrgTreeNode[]
  createdAt: string
  updatedAt: string
}

export interface OrgTreeResp {
  tree: OrgTreeNode[]
}

export interface OrgSaveReq {
  id?: number
  name: string
  parent_id: number
  sort: number
}

export interface OrgDeleteReq {
  id: number
}

export interface OrgStatusReq {
  id: number
  status: number
}

export interface OrgSimple {
  id: number
  name: string
}

export interface OrgSimpleListResp {
  list: OrgSimple[]
}

// 获取组织树
export const getOrgTree = () => {
  return http.request<OrgTreeResp>("get", "/api/v1/console/org/tree")
}

// 保存组织
export const saveOrg = (data: OrgSaveReq) => {
  return http.request("post", "/api/v1/console/org/save", { data })
}

// 删除组织
export const deleteOrg = (data: OrgDeleteReq) => {
  return http.request("post", "/api/v1/console/org/delete", { data })
}

// 更新组织状态
export const updateOrgStatus = (data: OrgStatusReq) => {
  return http.request("post", "/api/v1/console/org/updateStatus", { data })
}

// ==================== 课程属性相关API ====================

// 课程属性相关接口类型定义
export interface KnowledgeItem {
  id: number
  courseId: number
  name: string
  sort: number
  parentId: number
  requirement?: string
  createdTime?: number
  updatedTime?: number
  children?: KnowledgeItem[]
}

export interface TypesItem {
  id: number
  name: string
  category: number
  sort: number
  selectionMin: number
  selectionMax: number
  childrenCategory: number
  auto_correct: number
  createdTime?: number
  updatedTime?: number
}

export interface RankItem {
  id: number
  name: string
  sort: number
  createdTime?: number
  updatedTime?: number
}

export interface DiffItem {
  id: number
  name: string
  sort: number
  createdTime?: number
  updatedTime?: number
}

// 知识点树简化项（用于筛选选择）
export interface KnowledgeTreeItem {
  id: number
  name: string
  sort: number
  parentId: number
  children?: KnowledgeTreeItem[]
}

// 请求参数类型
export interface CourseKnowledgeReq {
  id?: number
  courseId: number
  name: string
  sort: number
  parentId: number
  requirement?: string
}

export interface CourseTypeReq {
  id?: number
  name: string
  category: number
  sort: number
  course_id: number
  disabled?: number
  selectionMin: number
  selectionMax: number
  childrenCategory: number
  auto_correct?: number
}

export interface CourseDiffReq {
  id?: number
  name: string
  sort: number
  courseId: number
  disabled?: number
}

export interface CourseRankReq {
  id?: number
  name: string
  sort: number
  courseId: number
  disabled?: number
}

// 课程属性 - 知识结构
export const getKnowledgeList = (courseId: number) => {
  return http.request<KnowledgeItem>("post", "/api/v1/console/knowledge/knowledgeTree", {
    data: { id: courseId }
  })
}

// 获取知识点树简化列表（用于筛选选择）
export const getKnowledgeTreeSimpleList = (courseId: number) => {
  return http
    .request<KnowledgeTreeItem>("post", "/api/v1/console/knowledge/knowledgeTree", {
      data: { id: courseId }
    })
    .then((response) => {
      // 将树形结构转换为扁平列表格式，以匹配其他API的返回格式
      const flattenTree = (node: KnowledgeTreeItem): KnowledgeTreeItem[] => {
        const result: KnowledgeTreeItem[] = [
          {
            id: node.id,
            name: node.name,
            sort: node.sort,
            parentId: node.parentId
          }
        ]
        if (node.children) {
          node.children.forEach((child) => {
            result.push(...flattenTree(child))
          })
        }
        return result
      }

      return {
        list: response ? flattenTree(response) : []
      }
    })
}

export interface SaveKnowledgeResp {
  id: number
}

export const saveKnowledge = (data: CourseKnowledgeReq) => {
  return http.request<SaveKnowledgeResp>("post", "/api/v1/console/knowledge/saveKnowledge", { data })
}

export const deleteKnowledge = (id: number) => {
  return http.request<any>("post", "/api/v1/console/knowledge/delKnowledge", {
    data: { id }
  })
}

// 课程属性 - 题型设置
export const getTypesList = (courseId: number) => {
  return http.request<{ list: TypesItem[] }>("post", "/api/v1/console/types/courseTypes", {
    data: { id: courseId }
  })
}

export const saveTypes = (data: CourseTypeReq) => {
  return http.request<any>("post", "/api/v1/console/types/saveType", { data })
}

export const deleteTypes = (id: number) => {
  return http.request<any>("post", "/api/v1/console/types/delType", {
    data: { id }
  })
}

// 课程属性 - 难度设置
export const getDiffList = (courseId: number) => {
  return http.request<{ list: DiffItem[] }>("post", "/api/v1/console/diff/courseDiffs", {
    data: { id: courseId }
  })
}

export const saveDiff = (data: CourseDiffReq) => {
  return http.request<any>("post", "/api/v1/console/diff/saveDiff", { data })
}

export const deleteDiff = (id: number) => {
  return http.request<any>("post", "/api/v1/console/diff/delDiff", {
    data: { id }
  })
}

// 课程属性 - 等级设置
export const getRankList = (courseId: number) => {
  return http.request<{ list: RankItem[] }>("post", "/api/v1/console/rank/courseRanks", {
    data: { id: courseId }
  })
}

export const saveRank = (data: CourseRankReq) => {
  return http.request<any>("post", "/api/v1/console/rank/saveRank", { data })
}

export const deleteRank = (id: number) => {
  return http.request<any>("post", "/api/v1/console/rank/delRank", {
    data: { id }
  })
}

// ==================== 课程配置相关 ====================

// 课程信息接口（匹配后端实际返回字段）
export interface CourseInfo {
  id: number
  name: string // 课程名称
  cat_id?: number // 学科ID
  need_audit?: number // 是否需要审核 1需要 2不需要
  auditors?: string // 审核人员ID列表（JSON字符串）
  pub_use?: number // 是否公开使用 1是 2否
  paper_header?: string // 卷头HTML内容
  creator_id?: number // 创建者ID
  created_time?: number // 创建时间
  updated_time?: number // 更新时间
}

// 用户简单信息接口
export interface UserSimpleItem {
  id: number
  account: string
  name: string
}

// 用户简单列表请求参数（当前后端API不支持参数）
export interface UserSimpleListReq {
  // 暂无参数，保留接口为将来扩展做准备
}

// 用户简单列表响应（后端直接返回数组）
export type UserSimpleListResp = UserSimpleItem[]

// 课程配置保存请求参数（匹配后端 CourseReq 结构）
export interface CourseConfigReq {
  id: number // 课程ID
  name: string // 课程名称
  paperHeader: string // 卷头HTML内容
  catId: number // 学科ID
  pubUse: number // 是否公开使用 1是 2否
  needAudit: number // 是否需要审核 1需要 2不需要
  auditors: number[] // 审核人ID列表
}

// 审核配置专用请求参数
export interface AuditConfigReq {
  courseId: number
  enabled: boolean
  auditors: number[]
}

// 获取课程信息
export const getCourseInfo = (courseId: number) => {
  return http.request<CourseInfo>("get", "/api/v1/console/course/info", {
    params: { id: courseId }
  })
}

// 保存课程配置
export const saveCourseConfig = (data: CourseConfigReq) => {
  return http.request<any>("post", "/api/v1/console/course/saveCourse", {
    data
  })
}

// 保存审核配置（新增专用接口）
export const saveAuditConfig = (data: AuditConfigReq) => {
  return http.request<any>("post", "/api/v1/console/course/saveAuditConfig", {
    data
  })
}

// 获取用户简单列表
export const getUserSimpleList = (params: UserSimpleListReq) => {
  return http.request<UserSimpleListResp>("get", "/api/v1/console/user/simpleList", {
    params
  })
}

// ==================== 系统配置 ====================
export interface SystemConfigItem {
  code: string
  value: string
}

export const setSystemConfig = (data: SystemConfigItem) => {
  return http.request("post", "/api/v1/console/system_config/setSystemConfig", { data })
}

export const getSystemConfig = (code: string) => {
  return http.request<SystemConfigItem>("get", "/api/v1/console/system_config/getSystemConfig", {
    params: { code }
  })
}

// 通用存储上传（返回 wangEditor 兼容结构或直接图片地址数组）
export const uploadStorage = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)
  return http.request<any>("post", "/api/v1/console/storage/upload", {
    data: formData,
    headers: { "Content-Type": "multipart/form-data" }
  })
}

// ==================== 命题配置 ====================
export interface PropositionConfig {
  gen_paper_ip_limit: string[]    // 生成试卷的设备IP限制
  upload_paper_ip_limit: string[] // 上传试卷的设备IP限制
  paper_compare: boolean          // 试卷比对 上传试卷修订稿与电子试卷进行比对
  paper_lock: boolean             // 考前锁定 试卷提交后，在考试完借钱，试卷处于保密锁定状态， 考后开可以放开查询
  expert_review: boolean          // 专家评审 启用后所有命题项目默认都需要经过专家评审环节
}

export const getPropositionConfig = () => {
  return http.request<PropositionConfig>("get", "/api/v1/console/proposition/config")
}

export const setPropositionConfig = (data: PropositionConfig) => {
  return http.request("post", "/api/v1/console/proposition/setConfig", { data })
}

// ==================== 命题监控相关 ====================
export interface PropositionMonitorReq {
  page: number
  page_size: number
  start_time?: number // 考试开始时间（Unix时间戳）
  end_time?: number // 考试结束时间（Unix时间戳）
  keyword?: string // 关键词 课程名称、试卷名称、组卷人姓名
  state?: number // 状态 1待教研室审核 2教研室审核驳回 3教研室审核通过 4保密锁定 5待专家评审 6专家评审驳回
}

export interface PaperPropositionConfig {
  lock_device: boolean // 锁定设备
  paper_compare: boolean // 试卷比对
  paper_lock: boolean // 考前锁定
  expert_review: boolean // 专家评审
}

export interface PaperPropositionMonitorItem {
  paper_id: number // 试卷ID
  course_name: string // 课程名称
  paper_name: string // 试卷名称
  creator: string // 组卷人
  submit_time: number // 提交日期（Unix时间戳）
  exam_date: number // 考试日期（Unix时间戳）
  state: number // 状态
  config: PaperPropositionConfig // 命题配置
  printout_url: string // 打印稿URL
}

export interface PropositionMonitorResp {
  total: number // 总数
  list: PaperPropositionMonitorItem[] // 列表
}

// 获取命题监控列表
export const getPropositionMonitor = (data: PropositionMonitorReq) => {
  return http.request<PropositionMonitorResp>("post", "/api/v1/console/proposition/monitor", { data })
}

// 更新试卷命题配置
export interface UpdatePaperPropositionConfigReq {
  paper_id: number // 试卷ID
  paper_proposition_config: PaperPropositionConfig // 命题配置
}

export const updatePaperPropositionConfig = (data: UpdatePaperPropositionConfigReq) => {
  return http.request("post", "/api/v1/console/proposition/updatePaperPropositionConfig", { data })
}

export interface PropositionExpertAuditReq {
  paper_id: number
  pass: boolean
  opinion: string
}

export const expertAudit = (data: PropositionExpertAuditReq) => {
  return http.request("post", "/api/v1/console/proposition/expertAudit", { data })
}

// ==================== 录题管理相关 ====================

// 题目状态枚举
export enum QuestionState {
  APPROVED = 1, // 入库
  DRAFT = 2, // 暂存
  ABNORMAL = 3 // 异常
}

// 题型类别枚举
export enum QuestionTypeCategory {
  SINGLE_CHOICE = 1, // 单选
  MULTIPLE_CHOICE = 2, // 多选
  BLANK_FILLING = 3, // 填空
  TRUE_FALSE = 4, // 判断
  COMPOSITE = 5, // 复合
  OTHER = 6 // 其他
}

// 录题列表查询参数
export interface MyQuestionListReq {
  page: number
  size: number
  courseId: number // 课程ID（必填）
  ids?: number[] // 题目ID数组（可选，支持多ID筛选）
  diffId?: number // 难度ID（可选）
  rankId?: number // 等级ID（可选）
  typeId?: number // 题型ID（可选）
  kpIds?: number[] // 知识点ID数组（可选）
  indexText?: string // 检索文本（可选）
  createBegin?: number // 创建开始时间戳（可选）
  createEnd?: number // 创建结束时间戳（可选）
  tag?: string // 标签筛选（可选）
  state?: number // 试题状态（可选）1入库 2暂存 3异常
  updateBegin?: number // 更新开始时间戳（可选）
  updateEnd?: number // 更新结束时间戳（可选）
  isDelete?: boolean // 是否显示回收站题目（可选）
}

// 录题列表项
export interface QuestionPageItem {
  id: number
  creatorId: number
  creator: string
  auditorId?: number
  auditor?: string
  auditTime?: number
  courseId: number
  qtTypeId: number
  qtTypeName: string
  qtTypeCat: number
  qtDiffId: number
  qtDiffName: string
  qtRankId: number
  qtRankName: string
  qtKpId: number
  qtKpName: string
  year?: number
  refCount?: number
  qtRequirement?: string // 层次目标
  state: number
  children?: string
  childrenDetail?: QuestionMixed[]
  source?: string
  area?: string
  answer: string
  blanks: number
  qtHtml: string // 题干HTML
  asHtml?: string // 答案HTML
  anHtml?: string // 分析HTML
  optionaHtml?: string // 选项A HTML
  optionbHtml?: string // 选项B HTML
  optioncHtml?: string // 选项C HTML
  optiondHtml?: string // 选项D HTML
  optioneHtml?: string // 选项E HTML
  optionfHtml?: string // 选项F HTML
  tag?: string
  createdTime: number
  updatedTime: number
  deletedTime?: number // 删除时间
}

// 小题信息
export interface QuestionMixed {
  id?: number
  parentId: number
  sort: number
  qtTypeCat: number // 小题题型类别
  answer: string
  blanks: number
  qtHtml: string
  asHtml?: string
  anHtml?: string
  optionaHtml?: string
  optionbHtml?: string
  optioncHtml?: string
  optiondHtml?: string
  optioneHtml?: string
  optionfHtml?: string
  createdTime?: number
  updatedTime?: number
}

// 录题列表响应
export interface MyQuestionListResp {
  total: number
  list: QuestionPageItem[]
}

// 题目保存请求参数
export interface QuestionSaveReq {
  id?: number // 题目ID（编辑时需要）
  courseId: number // 课程ID
  qtTypeId: number // 题型ID
  qtTypeCat: number // 题型类别
  qtDiffId: number // 难度ID
  qtRankId: number // 等级ID
  qtKpId: number // 知识点ID
  qtRequirement?: string // 目标层次
  year?: number // 题目年代
  area?: string // 地区
  source?: string // 来源
  answer: string // 标准答案
  qtHtml: string // 题干HTML
  asHtml?: string // 答案HTML
  anHtml?: string // 分析HTML
  blanks: number // 空数（填空题）或选项数（选择题）
  optionaHtml?: string // 选项A HTML
  optionbHtml?: string // 选项B HTML
  optioncHtml?: string // 选项C HTML
  optiondHtml?: string // 选项D HTML
  optioneHtml?: string // 选项E HTML
  optionfHtml?: string // 选项F HTML
  childs?: QuestionMixed[] // 复合题子题
}

// 题目删除请求参数
export interface QuestionDeleteReq {
  arr: number[]
}

// 恢复题目请求参数
export interface QuestionRestoreReq {
  id: number
}

// 题目提审请求参数
export interface QuestionSubmitReq {
  id: number
}

// ==================== 录题相关API方法 ====================

// 获取我的录题列表
export const getMyQuestionList = (data: MyQuestionListReq) => {
  return http.request<MyQuestionListResp>("post", "/api/v1/console/question/myList", { data })
}

// 获取我的题目详情
export const getMyQuestionDetail = (id: number) => {
  return http.request<QuestionPageItem>("get", "/api/v1/console/question/detail", {
    params: { id }
  })
}

// 保存我的题目
export const saveMyQuestion = (data: QuestionSaveReq) => {
  return http.request<any>("post", "/api/v1/console/question/save", { data })
}

// 删除我的题目
export const deleteMyQuestion = (data: QuestionDeleteReq) => {
  return http.request<any>("post", "/api/v1/console/question/del", { data })
}

// 恢复我的题目
export const restoreMyQuestion = (data: QuestionRestoreReq) => {
  return http.request<any>("post", "/api/v1/console/question/restore", { data })
}

// 为题目添加/更新标签
export interface QuestionAddTagReq {
  id: number
  tag: string
}

export const addQuestionTag = (data: QuestionAddTagReq) => {
  return http.request<any>("post", "/api/v1/console/question/addTag", { data })
}

// 批量修改题目属性
export interface QuestionBatchEditAttributeReq {
  qtArr: number[]
  qtRankId?: number
  qtDiffId?: number
  qtKpId?: number
  qtYear?: number
  qtSource?: string
  qtArea?: string
  tag?: string
  state?: number // 1入库 2暂存 3异常
}

export const batchEditQuestionAttribute = (data: QuestionBatchEditAttributeReq) => {
  return http.request<any>("post", "/api/v1/console/question/batchEditAttribute", { data })
}

// 编辑我的题目
export const editMyQuestion = (data: QuestionSaveReq) => {
  return http.request<any>("post", "/api/v1/console/question/edit", { data })
}

// 更新题目状态 1入库 2暂存 3异常
export interface QuestionUpdateStateReq {
  id: number
  state: number
}

export const updateQuestionState = (data: QuestionUpdateStateReq) => {
  return http.request<any>("post", "/api/v1/console/question/updateState", { data })
}

// 提审我的题目 - 后端无此接口，已废弃
// export const submitMyQuestion = (data: QuestionSubmitReq) => {
//   return http.request<any>("post", "/api/v1/console/question/submit", { data })
// }

// ==================== 审核相关API ====================

// 审核列表查询参数
export interface AuditQuestionListReq {
  page: number
  size: number
  courseId: number // 课程ID（必填）
  diffId?: number // 难度ID（可选）
  rankId?: number // 等级ID（可选）
  typeId?: number // 题型ID（可选）
  kpIds?: number[] // 知识点ID数组（可选）
  indexText?: string // 检索文本（可选）
  createBegin?: number // 创建开始时间戳（可选）
  createEnd?: number // 创建结束时间戳（可选）
  creatorName?: string // 录题人姓名（可选）
}

// 审核操作请求参数
export interface AuditActionReq {
  id: number
  reason?: string // 驳回原因（驳回时必填）
}

// 获取待审核题目列表
export const getAuditQuestionList = (data: AuditQuestionListReq) => {
  return http.request<MyQuestionListResp>("post", "/api/v1/console/question/auditList", { data })
}

// 通过审核
export const passQuestionAudit = (data: AuditActionReq) => {
  return http.request<any>("post", "/api/v1/console/question/auditPass", { data })
}

// 驳回审核
export const rejectQuestionAudit = (data: AuditActionReq) => {
  return http.request<any>("post", "/api/v1/console/question/auditReject", { data })
}

// ==================== 批量导入相关 ====================
export interface QuestionBatchImportReq {
  list: QuestionSaveReq[]
}

export const batchImportQuestions = (data: QuestionBatchImportReq) => {
  return http.request<any>("post", "/api/v1/console/question/batchImport", { data })
}

// ==================== 方案相关 ====================

// 方案筛选项类型
export interface SchemeFilter {
  nousedate?: number
  yearfrom?: number
  yearto?: number
  ranks?: number[]
}

// 方案详情设置类型
export interface SchemeDetailItem {
  knowledgeId: number
  typeId: number
  difficultyId: number
  count: number
  points: number
  children?: number[]
}

// 方案列表项类型
export interface SchemeItem {
  id: number
  name: string
  schemeType: string
  description: string
  courseId: number
  creatorId: number
  creator: string
  totalPoints: number
  akjf: number
  axtjf: number
  hlnd: number
  hlfz: number
  difficulty: number
  pub: number
  children: string
  schemeChildren?: SchemeItem[]
  genCount: number
  filterInfo: SchemeFilter
  filter: string
  createdTime: number
  updatedTime: number
}

// 方案保存请求类型
export interface SchemeSaveReq {
  id?: number
  name: string
  schemeType: string
  description: string
  courseId: number
  akjf: number
  axtjf: number
  hlnd: number
  hlfz: number
  difficulty: number
  pub: number
  filterInfo: SchemeFilter
  detail: SchemeDetailItem[]
}

// 方案详情类型
export interface SchemeInfo {
  id: number
  name: string
  description: string
  schemeType: string
  courseId: number
  creatorId: number
  creator: string
  totalPoints: number
  akjf: number
  axtjf: number
  hlnd: number
  hlfz: number
  difficulty: number
  pub: number
  children: string
  genCount: number
  filterInfo: SchemeFilter
  createdTime: number
  updatedTime: number
  detail: SchemeDetailItem[]
}

// 方案列表请求类型
export interface SchemeListReq {
  page: number
  size: number
  name?: string
  courseId: number
  creator?: number
  pub?: number
}

// 方案列表响应类型
export interface SchemeListResp {
  total: number
  list: SchemeItem[]
}

// 知识点计数请求类型
export interface SchemeCountReq {
  filter?: SchemeFilter
  kpArr: number[]
}

// 知识点计数响应类型
export interface SchemeCountResp {
  count: number
}

// 方案相关API方法
export const getSchemeList = (params: SchemeListReq) => {
  return http.request<SchemeListResp>("get", "/api/v1/console/scheme/list", { params })
}

export const getSchemeInfo = (id: number) => {
  return http.request<SchemeInfo>("post", "/api/v1/console/scheme/info", { data: { id } })
}

export const saveScheme = (data: SchemeSaveReq) => {
  return http.request<any>("post", "/api/v1/console/scheme/save", { data })
}

export const deleteScheme = (id: number) => {
  return http.request<any>("post", "/api/v1/console/scheme/delete", { data: { id } })
}

export const getKnowledgeCount = (data: SchemeCountReq) => {
  return http.request<SchemeCountResp>("post", "/api/v1/console/scheme/count", { data })
}

// ==================== 题型数量统计相关 ====================
export interface CountGroupItem {
  qtTypeCat: number // 题型类型 1单选 2多选 3填空 4判断 5复合 6其他
  qtTypeName: string // 题型名
  qtTypeId: number // 题型ID
  qtTypeSort: number // 题型顺序
  diffId?: number // 难度ID
  diffName?: string // 难度名
  diffSort?: number // 难度顺序
  count: number // 题量
}

export interface SchemeCountGroupReq {
  akjf: number // 按空计分
  axtjf: number // 按小题计分
  xxld: number // 详细难度
  kpIdArr: number[] // 知识点ID数组
  filter?: SchemeFilter // 详细筛选项
}

export interface SchemeCountGroupResp {
  code: number
  time: number
  msg: string
  data: {
    list: CountGroupItem[]
  }
}

export interface SchemeEnumerationReq {
  sum: number // 获取数量
  qtTypeId: number // 题型ID
  qtKpArr: number[] // 知识点ID数组
  schemeFilter: SchemeFilter // 筛选项
}

export interface SchemeEnumerationResp {
  code: number
  time: number
  msg: string
  data: {
    sum: number // 可选中的数量
  }
}

// 按题型查询题量
export const getSchemeCountWithGroup = (data: SchemeCountGroupReq) => {
  return http.request<SchemeCountGroupResp>("post", "/api/v1/console/scheme/countWithGroup", { data })
}

// 填空题/复合题按空积分/按小题积分获取离目标题量最近的题量数
export const getSchemeEnumeration = (data: SchemeEnumerationReq) => {
  return http.request<SchemeEnumerationResp>("post", "/api/v1/console/scheme/enumeration", { data })
}

// ==================== 随机组卷 - 立即组卷/保存多份 ====================
export interface GenPaperReq {
  schemeId: number
}

export interface GenPaperQuestionItem {
  id: number
  points: number
  qtTypeId: number
  qtDiffId: number
  qtRankId: number
  qtKpId: number
  qtTypeCat: number
  qtTypeName: string
  qtDiffName: string
  qtRankName: string
  qtKpName: string
  children: string
}

export interface GenPaperResp {
  list: GenPaperQuestionItem[]
}

export const genPaper = (data: GenPaperReq) => {
  return http.request<GenPaperResp>("post", "/api/v1/console/scheme/genPaper", { data })
}

export interface GenSavePaperReq {
  schemeId: number
  paperNames: string[]
  repeatRate?: number
}

export const genSavePaper = (data: GenSavePaperReq) => {
  return http.request<any>("post", "/api/v1/console/scheme/genSavePaper", { data })
}

// ==================== 考试学生信息相关 ====================
export interface StudentsInfoResp {
  list: ExamStudentsInfo[]
}

export interface ExamStudentsInfo {
  id: number
  examId: number
  stuId: number
  stuName: string
  stuAccount: string
  major: string
  group: string
  classId: number
  className: string
  state: number // 考试状态 1未登录 2考试中 3交卷 4缺考
  score: number // 总分 /100
  objectScore: number // 客观题分数 /100
  classRank: number // 班级排名
  totalRank: number // 全部排名
  pickTime: number // 领卷时间
  commitTime: number // 交卷时间
  createdTime: number
  updatedTime: number
}

// ==================== 学生试卷详情相关 ====================
export interface StudentPaperResp {
  exam: ExamItem
  details: StuPaperItem[]
}

export interface StuPaperItem {
  qtInfo: QtInfo
  ansInfo: AnsInfo
}

export interface QtInfo {
  id: number
  courseId: number
  qtTypeId: number
  qtType: string // 题型名
  qtTypeCat: number // 题型类型 1单选 2多选 3填空 4判断 5复合 6其他
  qtTypeName: string // 题型名
  qtDiffId: number
  qtDiffName: string // 难度名称
  qtRankId: number
  qtRankName: string // 等级名称
  qtKpId: number
  qtKpName: string // 知识点名称
  year: number
  refCount: number
  state: number // 试题状态 1入库 2暂存 3异常
  children: string // 小题ID数组 [1,2,3]
  childrenDetail: QtChild[]
  source: string
  area: string
  answer: string // 答案 判断题0/1 选择题ABC 填空题 xx,xx,xx 其他题目自定义
  blanks: number // 填空题 填空数量 选择题 选项数 复合题 小题数量
  qtHtml: string // 题干html question
  asHtml: string // 答案html answer
  anHtml: string // 分析html analysis
  optionaHtml: string // 选项Ahtml
  optionbHtml: string // 选项Bhtml
  optioncHtml: string // 选项CHtml
  optiondHtml: string // 选项Dhtml
  optioneHtml: string // 选项Ehtml
  optionfHtml: string // 选项Fhtml
  createdTime: number
  updatedTime: number
}

export interface QtChild {
  id: number
  parentId: number
  sort: number
  qtTypeCat: number // 题型类型 1单选 2多选 3填空 4判断 5复合 6其他
  answer: string
  blanks: number
  qtHtml: string
  asHtml: string
  anHtml: string
  optionaHtml: string
  optionbHtml: string
  optioncHtml: string
  optiondHtml: string
  optioneHtml: string
  optionfHtml: string
  createdTime: number
  updatedTime: number
}

export interface AnsInfo {
  id: number
  examId: number
  stuId: number
  qtId: number
  totalPoints: number // 总分 /100
  score: number // 得分 /100
  qtTypeId: number
  qtTypeCat: number // 题型类型 1单选 2多选 3填空 4判断 5复合 6其他
  qtTypeOrder: number
  qtOrder: number
  correctState: number // 考试状态 1未批改 2已批改
  answer: string // 答案信息
  createdTime: number
  updatedTime: number
}

// ==================== 班级/学生 管理（控制台） ====================
export interface ClassTreeNode {
  id: number
  name: string
  parent_id: number
  sort: number
  student_count: number
  children: ClassTreeNode[]
}

export interface ClassTreeResp {
  tree: ClassTreeNode[]
}

export interface ClassSaveReq {
  id?: number
  name: string
  parent_id?: number
  sort?: number
}

export const getClassTree = () => {
  return http.request<ClassTreeResp>("get", "/api/v1/console/class/tree")
}

export const saveClass = (data: ClassSaveReq) => {
  return http.request("post", "/api/v1/console/class/save", { data })
}

export const deleteClass = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/class/delete", { data })
}

export interface ConsoleStudentInfo {
  id: number
  name: string
  account: string
  class_id: number
  major?: string
  disabled: number
  created_time: number
  updated_time: number
}

export interface ConsoleStudentListReq {
  page?: number
  size?: number
  keyword?: string
  class_id?: number
  disabled?: number
}

export interface ConsoleStudentListResp {
  list: ConsoleStudentInfo[]
  total: number
}

export interface ConsoleStudentSaveReq {
  id?: number
  name: string
  account: string
  password?: string
  class_id: number
  major?: string
  disabled?: number
}

export const getStudentPageList = (data: ConsoleStudentListReq) => {
  return http.request<ConsoleStudentListResp>("post", "/api/v1/console/student/list", { data })
}

export const saveStudentForConsole = (data: ConsoleStudentSaveReq) => {
  return http.request("post", "/api/v1/console/student/save", { data })
}

export const deleteStudentForConsole = (data: { ids: number[] }) => {
  return http.request("post", "/api/v1/console/student/delete", { data })
}

// ==================== 考试-班级学生信息树 ====================
export interface StuInfo {
  stuId: number
  stuName: string
  stuAccount: string
  stuMajor: string
}

export interface ClassStuInfo {
  classId: number
  className: string
  children: ClassStuInfo[]
  students: StuInfo[]
}

export interface ClassStuResp {
  list: ClassStuInfo[]
}

export interface MajorsResp {
  list: string[]
}

export const getClassStuTree = () => {
  return http.request<ClassStuResp>("post", "/api/v1/console/exam/classStuTree")
}

export const getStuMajors = () => {
  return http.request<MajorsResp>("get", "/api/v1/console/exam/stuMajors")
}

export interface ResetMyPasswordReq {
  oldPassword: string
  newPassword: string
}

export function resetMyPassword(data: ResetMyPasswordReq) {
  return http.request("post", "/api/v1/console/auth/resetMyPassword", { data })
}

export interface UpdateMyPhoneReq {
  phone: string
}

export function updateMyPhone(data: UpdateMyPhoneReq) {
  return http.request("post", "/api/v1/console/auth/updateMyPhone", { data })
}

// ==================== 试卷方案类型设置 ====================
export interface PaperSchemeTypeItem {
  id: number
  name: string
  creator_id: number
  creator: string
  course_id: number
  sort: number
  created_time: number
}

export interface PaperSchemeTypeSaveReq {
  id?: number
  name: string
  course_id: number
  sort?: number
}

export const getPaperSchemeTypes = (courseId: number) => {
  return http.request<PaperSchemeTypeItem[]>("post", "/api/v1/console/paperSchemeTypes/list", {
    data: { id: courseId },
  })
}

export const savePaperSchemeType = (data: PaperSchemeTypeSaveReq) => {
  return http.request("post", "/api/v1/console/paperSchemeTypes/save", { data })
}

export const deletePaperSchemeType = (id: number) => {
  return http.request("post", "/api/v1/console/paperSchemeTypes/del", { data: { id } })
}

// ==================== 排行统计 ====================
export interface RankingStatisticsReq {
  rankType?: string // "all"(总榜), "lastYear"(去年), "thisYear"(今年)
  orgId?: number // 部门ID（可选）
  forceUpdate?: boolean // 强制刷新缓存
}

export interface TeacherRankItem {
  count: number // 录题量
  teacherId: number // 教师ID
  teacherName: string // 教师姓名
  departmentName: string // 所属部门
}

export interface CourseRankItem {
  count: number // 录题量
  courseId: number // 课程ID
  courseName: string // 课程名称
  departmentName: string // 开课单位
}

export interface RankingStatisticsResp {
  teacherRank: TeacherRankItem[] // 教师录题排行
  courseRank: CourseRankItem[] // 课程录题排行
}

export const getRankingStatistics = (data: RankingStatisticsReq) => {
  return http.request<RankingStatisticsResp>("post", "/api/v1/console/statistics/rankingStatistics", { data })
}

// ==================== 工作量统计 ====================
export interface WorkloadStatisticsReq {
  rankType?: string // "all"(总榜), "lastYear"(去年), "thisYear"(今年)
  forceUpdate?: boolean
}

export interface TeacherWorkloadItem {
  teacherId: number
  teacherName: string
  departmentName: string
  questionCreate: number // 录题量
  questionEdit: number   // 修改试题量
  paperCreate: number    // 组卷量
  paperAudit: number     // 审卷量
  total: number          // 合计
}

export interface CourseWorkloadItem {
  courseId: number
  courseName: string
  departmentName: string
  questionCreate: number
  questionEdit: number
  paperCreate: number
  paperAudit: number
  total: number
}

export interface WorkloadStatisticsResp {
  teacherList: TeacherWorkloadItem[]
  courseList: CourseWorkloadItem[]
}

export const getWorkloadStatistics = (data: WorkloadStatisticsReq) => {
  return http.request<WorkloadStatisticsResp>("post", "/api/v1/console/statistics/workloadStatistics", { data })
}

// ==================== 试卷统计 ====================
export interface PaperStatisticsReq {
  catId?: number // 学科ID（可选）
  forceUpdate?: boolean // 强制刷新缓存
}

export interface PaperStatisticsOverview {
  schemeCount: number // 组卷方案数量
  paperCount: number // 电子试卷数量
  perCountInPaper: number // 试卷平均题量
  generateCount: number // 组卷次数
  exportCount: number // 导出Word数
}

export interface PaperCourseItem {
  courseName: string // 课程名称
  schemeCount: number // 组卷方案数
  paperCount: number // 试卷数量
  perCountInPaper: number // 试卷平均题量
  generateCount: number // 组卷次数
  exportCount: number // 导出Word数
}

export interface PaperStatisticsResp {
  overview: PaperStatisticsOverview // 总览数据
  courseList: PaperCourseItem[] // 课程详细列表
}

export const getPaperStatistics = (data: PaperStatisticsReq) => {
  return http.request<PaperStatisticsResp>("post", "/api/v1/console/statistics/paperStatistics", { data })
}

// ==================== 考试分析 ====================
export interface ExamAnalysisReq {
  year?: number // 年份（可选）
  examType?: number // 考试类型 1正式考试 2练习自测 0全部（可选）
}

export interface ExamAnalysisItem {
  examId: number // 考试ID
  examName: string // 考试名称
  passRate: number // 及格率
  excellentRate: number // 优秀率
  absentRate: number // 缺考率
  topScore: number // 最高分
  bottomScore: number // 最低分
  averageScore: number // 平均分
}

export interface ExamAnalysisResp {
  list: ExamAnalysisItem[] // 考试分析列表
}

/** 获取考试分析数据 */
export const getExamAnalysis = (data: ExamAnalysisReq) => {
  return http.request<ExamAnalysisResp>("post", "/api/v1/console/exam/examAnalysis", { data })
}

// ==================== 考试试卷结构分析相关 ====================
export interface QtTypeStruct {
  qtTypeId: number // 题型ID
  qtTypeCat: number // 题型类型 1单选 2多选 3填空 4判断 5复合 6其他
  qtTypeName: string // 题型名称
  rate: number // 占比（百分比）
  totalScore: number // 总分
}

export interface QtRankStruct {
  qtRankId: number // 等级ID
  qtRankName: string // 等级名称
  rate: number // 占比（百分比）
  totalScore: number // 总分
}

export interface QtDiffStruct {
  qtDiffId: number // 难度ID
  qtDiffName: string // 难度名称
  rate: number // 占比（百分比）
  totalScore: number // 总分
}

export interface QtKnowledgeStruct {
  qtKnowledgeId: number // 知识点ID
  qtKnowledgeName: string // 知识点名称
  rate: number // 占比（百分比）
  totalScore: number // 总分
}

export interface ExamPaperStructAnalysisResp {
  qtTypeStruct: QtTypeStruct[] // 题型结构
  qtRankStruct: QtRankStruct[] // 等级结构
  qtDiffStruct: QtDiffStruct[] // 难度结构
  qtKnowledgeStruct: QtKnowledgeStruct[] // 知识点结构
}

/** 获取考试试卷结构分析 */
export const getExamPaperStructAnalysis = (examId: number) => {
  return http.request<ExamPaperStructAnalysisResp>("post", "/api/v1/console/exam/examPaperStructAnalysis", {
    data: { id: examId }
  })
}

// ==================== 考试试题得分分析相关 ====================
export interface ExamPaperScoreAnalysisItem {
  qtId: number // 题目ID
  qtTypeId: number // 题型ID
  qtTypeCat: number // 题型类型
  qtTypeName: string // 题型名称
  qtKpId: number // 知识点ID
  qtKpName: string // 知识点名称
  qtDiffId: number // 难度ID
  qtDiffName: string // 难度名称
  qtRankId: number // 等级ID
  qtRankName: string // 等级名称
  score: number // 分值
  averageScore: number // 平均分
  scoreRate: number // 分值占比
}

export interface ExamPaperScoreAnalysisResp {
  list: ExamPaperScoreAnalysisItem[] // 试题得分分析列表
}

/** 获取考试试题得分分析 */
export const getExamPaperScoreAnalysis = (examId: number, classId?: number) => {
  return http.request<ExamPaperScoreAnalysisResp>("post", "/api/v1/console/exam/examPaperScoreAnalysis", {
    data: { id: examId, classId: classId || 0 }
  })
}

// ==================== 考试题型得失分析相关 ====================
export interface ExamPaperQtTypeAnalysisItem {
  qtTypeId: number // 题型ID
  qtTypeCat: number // 题型类型
  qtTypeName: string // 题型名称
  totalScore: number // 总分值
  averageScore: number // 平均分
  scoreRate: number // 得分率
}

export interface ExamPaperQtTypeAnalysisResp {
  list: ExamPaperQtTypeAnalysisItem[] // 题型得失分析列表
}

/** 获取考试题型得失分析 */
export const getExamPaperQtTypeAnalysis = (examId: number, classId?: number) => {
  return http.request<ExamPaperQtTypeAnalysisResp>("post", "/api/v1/console/exam/examPaperQtTypeAnalysis", {
    data: { id: examId, classId: classId || 0 }
  })
}

// ==================== 考试知识点得失分析相关 ====================
export interface ExamPaperKnowledgeAnalysisItem {
  qtKpId: number // 知识点ID
  qtKpName: string // 知识点名称
  totalScore: number // 总分值
  averageScore: number // 平均分
  scoreRate: number // 得分率
}

export interface ExamPaperKnowledgeAnalysisResp {
  list: ExamPaperKnowledgeAnalysisItem[] // 知识点分析列表
}

/** 获取考试知识点得失分析 */
export const getExamPaperKnowledgeAnalysis = (examId: number, classId?: number) => {
  return http.request<ExamPaperKnowledgeAnalysisResp>("post", "/api/v1/console/exam/examPaperKnowledgeAnalysis", {
    data: { id: examId, classId: classId || 0 }
  })
}

// ==================== 考试班级学员信息相关 ====================
export interface ExamClassStuInfo {
  stuId: number // 学生ID
  stuName: string // 学生姓名
  stuAccount: string // 学生学号
  major: string // 学生专业名称
  state: number // 考试状态 1未登录 2考试中 3交卷 4缺考
  score: number // 总分 /100
  objectScore: number // 客观题分数 /100
  classRank: number // 班级排名
  totalRank: number // 全部排名
}

export interface ClassWithStudents {
  classId: number // 班级ID
  className: string // 班级名称
  students: ExamClassStuInfo[] // 学生列表
}

export interface ExamStudentsInfoResp {
  classes: ClassWithStudents[]
}

/** 获取本场考试班级学员信息 */
export const getExamClassStudentsInfo = (examId: number) => {
  return http.request<ExamStudentsInfoResp>("post", "/api/v1/console/exam/examStudentsInfo", {
    data: { id: examId }
  })
}

// ==================== 班级分析（题型 & 知识点对比）====================
export interface ClassTypeAnalysisItem {
  qtTypeId: number
  qtTypeName: string
  totalScore: number // /100
  avgScore: number   // /100
}

export interface ClassTypeAnalysis {
  classId: number
  className: string
  items: ClassTypeAnalysisItem[]
}

export interface ClassKpAnalysisItem {
  kpId: number
  kpName: string
  totalScore: number // /100
  avgScore: number   // /100
}

export interface ClassKpAnalysis {
  classId: number
  className: string
  items: ClassKpAnalysisItem[]
}

export interface ClassAnalysisResp {
  typeAnalysis: ClassTypeAnalysis[]
  kpAnalysis: ClassKpAnalysis[]
}

/** 班级分析-题型和知识点对比 */
export const getExamClassAnalysis = (examId: number) => {
  return http.request<ClassAnalysisResp>("post", "/api/v1/console/exam/classAnalysis", {
    data: { id: examId }
  })
}

// ==================== 学生个人题型和知识点分析 ====================
export interface ExamPaperQtTypeAnalysisItemWithStudentScore {
  qtTypeId: number
  qtTypeCat: number
  qtTypeName: string
  totalScore: number
  averageScore: number // 全场平均分
  scoreRate: number
  studentScore: number // 学生得分
}

export interface ExamPaperKnowledgeAnalysisItemWithStudentScore {
  qtKpId: number
  qtKpName: string
  totalScore: number
  averageScore: number // 全场平均分
  scoreRate: number
  studentScore: number // 学生得分
}

export interface QtTypeAndKnowledgeAnalysisByStudentIdResp {
  typeList: ExamPaperQtTypeAnalysisItemWithStudentScore[]
  kpList: ExamPaperKnowledgeAnalysisItemWithStudentScore[]
}

/** 获取学生个人题型和知识点分析 */
export const getQtTypeAndKnowledgeAnalysisByStudentId = (examId: number, stuId: number) => {
  return http.request<QtTypeAndKnowledgeAnalysisByStudentIdResp>("post", "/api/v1/console/exam/qtTypeAndKnowledgeAnalysisByStudentId", {
    data: { examId, stuId }
  })
}

// ==================== 学生横向考试趋势 ====================
export interface HorizontalTrendItem {
  examId: number
  examName: string
  score: number // 本次得分 /100
  examAvgScore: number // 本次平均分 /100
  classRank: number
  totalRank: number
}

export interface HorizontalTrendByStudentIdResp {
  list: HorizontalTrendItem[]
}

/** 获取学生横向考试趋势 */
export const getHorizontalTrendByStudentId = (stuId: number) => {
  return http.request<HorizontalTrendByStudentIdResp>("post", "/api/v1/console/exam/horizontalTrendByStudentId", {
    data: { id: stuId }
  })
}

// ==================== MaxKey管理 ====================
export interface MaxKeyUserItem {
  id: string
  username: string
  displayName: string
  employeeNumber: string
  userType?: string
  departmentName?: string
}

export interface MaxKeyUserListReq {
  page: number
  size: number
  username?: string // 按用户名、展示名、员工号等搜索
  userType?: string
  departmentName?: string
}

export interface MaxKeyUserListResp {
  list: MaxKeyUserItem[]
  total: number
}

// 获取MaxKey用户列表
export const getMaxKeyUserList = (data: MaxKeyUserListReq) => {
  return http.request<MaxKeyUserListResp>("post", "/api/v1/console/maxkey/userList", {
    data
  })
}

// ==================== 题卷库预警 ====================
export interface StatisticsSetting {
  id: number
  course_id: number
  question_update_rate: number
  question_count_date: string
  question_used_count_rate: number
  question_old_count_rate: number
  question_as_old_year: number
  paper_update_rate: number
  paper_count_date: string
  paper_used_count_rate: number
  paper_old_count_rate: number
  paper_as_old_year: number
}

export interface CourseQuestionAndPaperStatsReq {
  course_name?: string
  group_name?: string
  order_by?: string
  only_alert?: boolean
  page?: number
  size?: number
}

export interface CourseQuestionAndPaperStatsItem {
  course_id: number
  course_name: string
  group_name: string
  course_code: string
  course_type: string
  update_rate: number
  used_rate: number
  old_rate: number
}

export interface CourseQuestionAndPaperStatsResp {
  list: CourseQuestionAndPaperStatsItem[]
  total: number
  group_names: string[]
}

export const getStatisticsSetting = () => {
  return http.request<StatisticsSetting>("get", "/api/v1/console/statistics/statisticsSetting")
}

export const setStatisticsSetting = (data: StatisticsSetting) => {
  return http.request("post", "/api/v1/console/statistics/setStatisticsSetting", { data })
}

export const getCourseQuestionAndPaperStatistics = (data: CourseQuestionAndPaperStatsReq) => {
  return http.request<CourseQuestionAndPaperStatsResp>("post", "/api/v1/console/statistics/courseQuestionAndPaperStatistics", { data })
}

// ==================== 教务中台第三方数据同步 ====================
export interface JxxlClassItem {
  bjmc: string // 班级名称
  bh: string   // 编号
}

export interface JxxlClassListResp {
  list: JxxlClassItem[]
  total: number
}

export interface JxxlStudentItem {
  bjmc: string // 班级名称
  xh: string   // 学号
  xm: string   // 姓名
  zymc: string // 专业名称
}

export interface JxxlStudentListResp {
  list: JxxlStudentItem[]
  total: number
}

// 获取教务中台班级列表（一次性全量）
export const getJxxlClassList = () => {
  return http.request<JxxlClassListResp>("get", "/api/v1/console/jxxl/classList", {
    params: { offset: 0, size: 99999 }
  })
}

// 获取教务中台学员列表（一次性全量）
export const getJxxlStudentList = (key?: string) => {
  return http.request<JxxlStudentListResp>("get", "/api/v1/console/jxxl/studentList", {
    params: { offset: 0, size: 99999, key }
  })
}

// 教务中台-课程项
export interface JxxlCourseItem {
  kch: string   // 课程号
  kcmk: string  // 课程模块
  zxs: string   // 总学时
  sjxs: string  // 实践学时
  kcxz: string  // 课程性质
  llxs: string  // 理论学时
  sydx: string  // 适用对象
  kcmc: string  // 课程名称
  kkyx: string  // 开课院系
}

export interface JxxlCourseListResp {
  list: JxxlCourseItem[]
  total: number
}

// 获取教务中台课程列表（一次性全量）
export const getJxxlCourseList = () => {
  return http.request<JxxlCourseListResp>("get", "/api/v1/console/jxxl/courseList", {
    params: { offset: 0, size: 99999 }
  })
}

// ==================== 消息通知 ====================
export interface MessageItem {
  id: number
  teacherId: number
  messageType: number
  relationId: number
  sender: number
  senderName: string
  title: string
  comment: string
  isRead: number
  createdTime: number
}

export interface MessageListResp {
  list: MessageItem[]
  total: number
}

// 获取消息列表
export const getMessageList = (params: { page: number; size: number; isRead?: number }) => {
  return http.request<MessageListResp>("post", "/api/v1/console/messgae/list", { params })
}

// 设置消息已读
export const setMessageRead = (data: { id: number }) => {
  return http.request("post", "/api/v1/console/messgae/setRead", { data })
}

// 设置所有消息已读
export const setMessageReadAll = () => {
  return http.request("get", "/api/v1/console/messgae/setReadAll")
}

// 获取教师联系电话
export const getTeacherPhone = (id: number) => {
  return http.request<{ phone: string }>("get", "/api/v1/console/user/getPhone", { params: { id } })
}
